<template>
  <article>
    <MastheadAlt
      subtitle="Link in bio for Agencies"
      title="Prove your ROI. Learn from your best month with insights."
      text="Get detailed insights into your link in bio traffic with with Pro+
            and advanced analytics."
      ctaText="(10-day trial included with every paid plan)"
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started</Button
      >
      <Button variant="outline" to="/demo" size="large">Request a Demo</Button>
    </MastheadAlt>

    <SeenOn />

    <TestimonialSection
      title="We are loving campsite"
      img-src="/campsites/small-business/campsite-smallstudio.png"
      name="Ashton Henry, CEO, Small Studio"
    >
      <p>
        We are loving campsite and have started integrating it for all of our
        clients as well. We love the analytics we get on our pro accounts that
        help us to determine what is truly working on social media. We are able
        to take that information from previous months and use it to create
        successful data driven strategies! If you're a social media manager or
        just someone who wants to better understand how to make social media
        work harder for your business, you need campsite.
      </p>
    </TestimonialSection>

    <LargeTextSection
      >As an agency your clients want to know their ROI. Campsite.bio gathers
      all of the important data you need. Wouldn't it be great if you could pull
      a report together fast and show where you've optimized?</LargeTextSection
    >

    <FeatureSection
      title="Our actionable reports give you all of the insights at your
          fingertips"
    >
      <VText slot="text"
        >Campsite.bio makes your job easier with our advanced reports, profile
        management tools, analytics and more. Focus more on your clients
        success, and less on data gathering.</VText
      >

      <FeatureCard
        span="2"
        title="Reporting tools"
        bg-color="secondary"
        color="secondary"
      >
        <LineChartIcon slot="icon" />
        <VText
          >We're constantly gathering data on what is happening on your
          profiles. We dissect and present your data in five different reports
          to help you gain insights. It's easy to report back your link in bio
          performance and make improvements.
        </VText>
        <img
          slot="img"
          src="~/assets/images/use-cases/smm/leaderboard-report.png"
          loading="lazy"
          alt="Leaderboard report"
        />
      </FeatureCard>
      <FeatureCard title="Profile management">
        <AccountMultipleIcon slot="icon" />
        <VText
          >Manage all of your clients under a single account with a single
          invoice. Add, remove and switch between profiles with ease.
        </VText>
      </FeatureCard>
      <FeatureCard title="Collaborators">
        <PersonAddIcon slot="icon" />
        <VText
          >Share the workload by inviting team members or clients to manage
          profiles. Control what they can access with different permissions.
        </VText>
      </FeatureCard>
    </FeatureSection>

    <FeatureSection
      title="Unlock our best with the Pro+ subscription"
      bg-color="gray100"
    >
      <VText slot="text"
        >Get all the features you need in a link in bio app with our Pro+
        subscription.</VText
      >
      <Button slot="cta" to="/pricing">Compare features</Button>

      <FeatureCard title="Six profiles">
        <AccountMultipleIcon slot="icon" />
        <VText
          >Get all of your clients added with built-in support for six profiles.
          Need more? Modify your plan to support more for an additional cost.
        </VText>
      </FeatureCard>
      <FeatureCard title="Four collaborators">
        <PersonAddIcon slot="icon" />
        <VText
          >Add your account manager or clients to help manage your profiles.
          Four are included, with the ability to add more for an additional
          cost.
        </VText>
      </FeatureCard>
      <FeatureCard title="Premium link types">
        <GridIcon slot="icon" />
        <VText
          >Unlock our best link types with multiple carousels, opt-in forms,
          feed links, forms (Typeform and Jotform), image grids and so much
          more.</VText
        >
      </FeatureCard>
      <FeatureCard title="Automation">
        <SettingsIcon slot="icon" />
        <VText
          >Keep your profile fresh with our automated link types, like the feed
          and image grid. They'll pull in your newest content, automatically.
        </VText>
      </FeatureCard>
      <FeatureCard title="Reports">
        <FileDownloadIcon slot="icon" />
        <VText
          >Access five different reports that slice and dice your profile data
          so you can make improvements quickly and report back performance with
          ease.
        </VText>
      </FeatureCard>
      <FeatureCard title="Filtering & Exports">
        <FilterIcon slot="icon" />
        <VText
          >Filter down our analytics by country, device, referrer, or campaign.
          You can even download your analytics data to dive deeper into it.
        </VText>
      </FeatureCard>
    </FeatureSection>

    <RaisedCtaSection
      title="Simplify your link in bio management today"
      text="All of your accounts in one easy to manage place. Make you and your client happier."
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  Button,
  LineChartIcon,
  AccountMultipleIcon,
  PersonAddIcon,
  GridIcon,
  SettingsIcon,
  FilterIcon,
  FileDownloadIcon,
} from '@campsite-bio/component-lib';

import SeenOn from '@/components/seen-on-analytics';
import TestimonialSection from '@/components/testimonials/testimonial-section';
import FeatureCard from '@/components/features/feature-card';
import FeatureSection from '@/components/features/feature-section';
import RaisedCtaSection from '@/components/sections/raised-cta-section';
import LargeTextSection from '@/components/sections/large-text-section';
import MastheadAlt from '@/components/sections/masthead-alt';
import { generateMeta } from '~/utils/generate-meta';

export default {
  components: {
    VText,
    Button,
    SeenOn,
    TestimonialSection,
    FeatureCard,
    FeatureSection,
    LineChartIcon,
    AccountMultipleIcon,
    PersonAddIcon,
    GridIcon,
    SettingsIcon,
    FilterIcon,
    RaisedCtaSection,
    LargeTextSection,
    MastheadAlt,
    FileDownloadIcon,
  },

  head() {
    return generateMeta({
      title: 'Link in bio for Agencies - Campsite.bio',
      description:
        'Prove your ROI on your link in bio. Learn more about the features Campsite.bio offers to agencies.',
    });
  },
};
</script>

<style lang="scss" scoped></style>
